import React, { useEffect, useState } from 'react'
import { getUserProfile } from '../../api/user/account'
import { useTranslation } from 'react-i18next'
import './accountbilling.css'
import { Link } from 'react-router-dom'
import Loader from '../common/Loader'

const AccountBilling = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [user, setUser] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const { t } = useTranslation()

  useEffect(() => {
    let timer
    // Fetch user profile data
    const fetchUserProfile = async () => {
      try {
        setIsLoading(true)
        const response = await getUserProfile()
        if (response.success) {
          setUser(response.user)
        } else {
          setError(t('account_billing.failed_load'))
        }
      } catch (error) {
        setError(error.message || t('account_billing.error_occurred'))
      } finally {
        setIsLoading(false)
        // Small delay to ensure CSS is loaded and applied
        timer = setTimeout(() => {
          setIsVisible(true)
        }, 50)
      }
    }

    fetchUserProfile()
    return () => clearTimeout(timer)
  }, [t])

  // Show loader while loading
  if (isLoading) {
    return <Loader />
  }

  // Show error message if there's an error
  if (error && !user) {
    return (
      <div className='container account-billing-container'>
        <h2>{t('account_billing.title')}</h2>
        <div className="error-message">
          <i className="fa fa-exclamation-circle"></i>
          {error}
          <button onClick={() => window.location.reload()} className="retry-btn">
            {t('account_billing.retry')}
          </button>
        </div>
      </div>
    )
  }

  const firstName = user?.first_name || ''
  const lastName = user?.last_name || ''
  const email = user?.email || ''
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U'

  const options = [
    {
      to: '/accountbilling/personal-details',
      icon: 'fa-user',
      title: t('account_billing.personal_info'),
      desc: t('account_billing.personal_info_desc'),
      accent: 'blue',
    },
    {
      to: '/accountbilling/login-security',
      icon: 'fa-shield',
      title: t('account_billing.login_security'),
      desc: t('account_billing.login_security_desc'),
      accent: 'green',
    },
    {
      to: '/update-account',
      icon: 'fas fa-sync',
      title: t('account_billing.update_account'),
      desc: t('account_billing.update_account_desc'),
      accent: 'purple',
    },
  ]

  return (
    <div className='account-billing-page'>
      <div className='ab-page-bg' aria-hidden="true">
        <div className="ab-blob ab-blob-1" />
        <div className="ab-blob ab-blob-2" />
        <div className="ab-blob ab-blob-3" />
      </div>

      <div className='container account-billing-container'>
        {/* Hero header */}
        <header className={`ab-hero ${isVisible ? 'safe-fade-in' : ''}`}>
          <div className="ab-hero-avatar">
            <span className="ab-avatar-ring" aria-hidden="true" />
            <div className="ab-avatar">{initials}</div>
          </div>
          <div className="ab-hero-text">
            <p className="ab-hero-eyebrow">{t('account_billing.title')}</p>
            <h1 className="ab-hero-title">
              {firstName || lastName ? (
                <>
                  {t('account_billing.welcome_back')}
                  <span className="ab-hero-name"> {firstName || lastName}</span>
                </>
              ) : (
                t('account_billing.welcome_back')
              )}
            </h1>
            <p className="ab-hero-subtitle">{email}</p>
          </div>
          <Link to='/accountbilling/personal-details' className="ab-hero-cta">
            <i className="fa fa-user-edit" aria-hidden="true" />
            {t('account_billing.go_profile')}
          </Link>
        </header>

        {/* Options cards */}
        <div className="account-billing-options">
          {options.map((opt, index) => (
            <Link
              key={opt.to}
              to={opt.to}
              className={`links ab-card-wrap ${isVisible ? 'safe-fade-in' : ''}`}
              style={{ animationDelay: `${0.08 * index + 0.12}s` }}
            >
              <div className={`ab-option ab-accent-${opt.accent}`}>
                <div className="ab-option-icon">
                  <i className={`fa ${opt.icon}`} aria-hidden="true" />
                </div>
                <div className="ab-option-body">
                  <h2>{opt.title}</h2>
                  <p>{opt.desc}</p>
                </div>
                <div className="ab-option-arrow">
                  <i className="fa fa-arrow-right" aria-hidden="true" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}

export default AccountBilling
